import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from 'axios';
import { PHONE_HELPER, blockNonDigitKey, onlyDigits } from '../../utils/validation';

const CODE_TTL = 60;

/**
 * 휴대폰 SMS 본인확인.
 * 인증번호 유효시간 60초, 재발송도 60초 후부터 가능(문자 비용/스팸 방지).
 * 인증에 성공하면 onVerified(token)으로 본인확인 토큰을 넘긴다.
 */
export default function PhoneVerification({
  phone,
  onPhoneChange,
  verified,
  onVerified,
  onReset,
  error,
  label = '연락처',
}) {
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [seconds, setSeconds] = useState(0);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => clearInterval(timerRef.current), []);

  const startCountdown = () => {
    clearInterval(timerRef.current);
    setSeconds(CODE_TTL);
    timerRef.current = setInterval(() => {
      setSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handlePhoneChange = (event) => {
    const next = onlyDigits(event.target.value);
    onPhoneChange(next);
    if (verified) onReset?.();
    setCodeSent(false);
    setCode('');
    setMessage('');
    clearInterval(timerRef.current);
    setSeconds(0);
  };

  const sendCode = () => {
    if (!/^[0-9]{10,11}$/.test(phone || '')) {
      setMessage("휴대폰 번호를 '-' 없이 숫자만 입력해주세요.");
      return;
    }
    setSending(true);
    setMessage('');
    axios
      .post('/estimate/verify/send', { phone })
      .then(() => {
        setCodeSent(true);
        setCode('');
        startCountdown();
        setMessage('인증번호를 문자로 보냈습니다. 60초 안에 입력해주세요.');
      })
      .catch((err) =>
        setMessage(err?.response?.data?.message || '인증번호 발송에 실패했습니다. 잠시 후 다시 시도해주세요.')
      )
      .finally(() => setSending(false));
  };

  const confirmCode = () => {
    if (code.length !== 6) {
      setMessage('인증번호 6자리를 입력해주세요.');
      return;
    }
    setSending(true);
    axios
      .post('/estimate/verify/confirm', { phone, code })
      .then((response) => {
        clearInterval(timerRef.current);
        setSeconds(0);
        setCodeSent(false);
        setMessage('');
        onVerified(response.data.verificationToken);
      })
      .catch((err) =>
        setMessage(err?.response?.data?.message || '인증번호가 일치하지 않습니다.')
      )
      .finally(() => setSending(false));
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
        <TextField
          fullWidth
          size='small'
          type='tel'
          label={label}
          name='phone'
          value={phone || ''}
          onChange={handlePhoneChange}
          onKeyDown={blockNonDigitKey}
          inputProps={{ maxLength: 11, inputMode: 'numeric' }}
          disabled={verified}
          error={Boolean(error)}
          helperText={error || PHONE_HELPER}
          required
          sx={{ backgroundColor: '#FCFCFC' }}
        />
        <Button
          onClick={sendCode}
          disabled={verified || sending || seconds > 0}
          variant='contained'
          sx={{
            minWidth: { xs: '100%', sm: 152 },
            height: 40,
            backgroundColor: verified ? '#2E7D32' : '#1B3C7A',
            color: '#fff',
            fontWeight: 800,
            fontSize: 15,
            whiteSpace: 'nowrap',
            boxShadow: '0 3px 10px rgba(27, 60, 122, 0.28)',
            '&:hover': { backgroundColor: verified ? '#2E7D32' : '#122C5C' },
            '&.Mui-disabled': {
              backgroundColor: verified ? '#2E7D32' : '#9aa7bd',
              color: '#fff',
              opacity: 1,
            },
          }}
        >
          {verified ? '인증완료' : seconds > 0 ? `재발송 ${seconds}초` : '인증번호 받기'}
        </Button>
      </Stack>

      {codeSent && !verified && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            size='small'
            label='인증번호 6자리'
            value={code}
            onChange={(event) => setCode(onlyDigits(event.target.value))}
            onKeyDown={blockNonDigitKey}
            inputProps={{ maxLength: 6, inputMode: 'numeric' }}
            helperText={seconds > 0 ? `남은 시간 ${seconds}초` : '유효시간이 지났습니다. 다시 받아주세요.'}
            sx={{ backgroundColor: '#FCFCFC' }}
          />
          <Button
            onClick={confirmCode}
            disabled={sending || seconds === 0}
            variant='outlined'
            sx={{
              minWidth: { xs: '100%', sm: 152 },
              height: 40,
              color: '#1B3C7A',
              borderColor: '#1B3C7A',
              borderWidth: 2,
              fontWeight: 800,
              fontSize: 15,
              '&:hover': { borderWidth: 2, borderColor: '#122C5C', backgroundColor: '#eef3fb' },
            }}
          >
            확인
          </Button>
        </Stack>
      )}

      {verified && (
        <Alert
          icon={<CheckCircleIcon fontSize='small' />}
          severity='success'
          sx={{ mt: 1, py: 0.2 }}
        >
          휴대폰 본인확인이 완료되었습니다.
        </Alert>
      )}

      {message && !verified && (
        <Typography sx={{ mt: 0.8, fontSize: 13, color: '#b3261e', wordBreak: 'keep-all' }}>
          {message}
        </Typography>
      )}
    </Box>
  );
}
